import React, { useState } from 'react';
import { Button, Grid, Dialog, DialogActions, DialogContent, DialogContentText, TextField, DialogTitle, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material";
import Busy from "../../../../Busy";
import {
  requestCompleteRefundWithDeduction,
  requestCompleteRefundWithoutDeduction,
  requestDenyRefund
} from "../../../request/invoiceRequests";
import { CallbackActions } from "../../../../../util/ActionUtils";
import {getErrorMessage} from "../../../../../util/NetworkErrorUtil";
import {useSnackbar} from "../../../../../context/snackbar-context";

interface RefundInvoice {
  transactionId: string;
  transactionNumber?: string | number;
  locationIsLive?: boolean;
}

interface RefundRequestResponseOption {
  test: (invoice?: RefundInvoice) => boolean;
  label: string;
  requester: typeof requestCompleteRefundWithDeduction;
  submitLabel: string;
  success: string;
  testFailure: string;
}

const RefundRequestResponseOptions: RefundRequestResponseOption[] = [
  {
    test: (invoice) => !!invoice?.locationIsLive,
    label: 'Complete Refund (Deduct From Next Payout)',
    requester: requestCompleteRefundWithDeduction,
    submitLabel: 'Approve Refund',
    success: 'Successfully completed refund! Refund Invoice: ',
    testFailure: 'Location is no longer live. Manually Invoice.'
  },
  {
    test: (invoice) => !!invoice,
    label: 'Complete Refund (DO NOT Deduct From Next Payout',
    requester: requestCompleteRefundWithoutDeduction,
    submitLabel: 'Approve Refund',
    success: 'Successfully completed refund! Refund Invoice: ',
    testFailure: ''
  },
  {
    test: (invoice) => !!invoice,
    label: 'Deny Refund',
    requester: requestDenyRefund,
    submitLabel: 'Deny Refund',
    success: 'Successfully denied refund! Refund Invoice: ',
    testFailure: ''
  },
];

interface ManageRequestRefundModalProps {
  invoice?: RefundInvoice;
  open: boolean;
  onInvoiceAction: (callbackAction?: string, invoice?: RefundInvoice, message?: string) => void;
  onClose: (callbackAction?: string, invoice?: RefundInvoice, message?: string) => void;
}

const ManageRequestRefundModal = (props: ManageRequestRefundModalProps) => {
  const { invoice, open, onInvoiceAction, onClose } = props;
  const { snackbarShowMessage } = useSnackbar();
  const [submitLabel, setSubmitLabel] = useState('');

  const handleClose = () => {
    onClose();
  };

  const onRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const index = parseInt(event.target.value);
    setSubmitLabel(RefundRequestResponseOptions[index].submitLabel);
  }

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const index = parseInt(formData.get('refundRequestResponse') as string);
    const requester = RefundRequestResponseOptions[index].requester;
    const reasonValue = formData.get('reason') as string | null;
    const reason = reasonValue ? reasonValue.trim() : '';

    if (!invoice) {
      return;
    }
    if (!reason) {
      snackbarShowMessage('Reason is required.', 'error');
      return;
    }
    Busy.set(true);
    requester(invoice, reason).then(() => {
      onClose(CallbackActions.SUCCESS, invoice, RefundRequestResponseOptions[index].success + invoice?.transactionNumber);
    }).catch((error) => {
      onInvoiceAction(CallbackActions.ERROR, invoice, getErrorMessage(error));
    }).finally(() => {
      Busy.set(false);
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        component: 'form',
        onSubmit
      }}
    >
      <DialogTitle>
        Respond to Refund Request
      </DialogTitle>
      <DialogContent>
        <RadioGroup
          name={"refundRequestResponse"}
          onChange={onRadioChange}
        >
          {RefundRequestResponseOptions.map((refundRequestResponseOption, index) => (
            <React.Fragment key={refundRequestResponseOption.label}>
              <FormControlLabel
                value={index}
                label={refundRequestResponseOption.label}
                control={<Radio disabled={!refundRequestResponseOption.test(invoice)}/>}
              />
              {!refundRequestResponseOption.test(invoice) && <Typography sx={{ ml: 3.5 }} variant="subtitle2" color="error">{refundRequestResponseOption.testFailure}</Typography>}
            </React.Fragment>
          ))}
        </RadioGroup>
          <DialogContentText>
            Please provide an explanation.
          </DialogContentText>
          <TextField
            required
            margin="dense"
            id="outlined-multiline-static"
            name="reason"
            label="Reason for Refund"
            helperText="Enter the reason this invoice is being refunded."
            multiline
            rows={2}
            fullWidth
            variant="standard"
          />
      </DialogContent>
      <DialogActions>
        <Grid container justifyContent={"center"}>
          <Button variant="text" onClick={handleClose} >Cancel</Button>
          {invoice && submitLabel && <Button variant="contained" type="submit" >{submitLabel}</Button>}
        </Grid>
      </DialogActions>
    </Dialog>
  );
}
export default ManageRequestRefundModal;
